// ─────────────────────────────────────────────────────────────────────────────
// feeUI.jsx
// Shared design tokens, formatters, and presentational primitives for the Fee
// Management module. Imported by FeeManagement.js and FeeSetupView.jsx so the
// two stay visually identical and there is a single source of truth.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";

// ── 1. Design tokens (matches ALS Portal brand palette — src/index.css :root) ──
export const C = {
  navy:      "#0f1d36",
  navyMid:   "#1c3461",
  blue:      "#2a4a7f",
  blueSoft:  "rgba(30,58,95,0.10)",
  red:       "#c41e24",
  redSoft:   "rgba(196,30,36,0.08)",
  green:     "#2e7d32",
  greenSoft: "rgba(46,125,50,0.10)",
  greenBdr:  "rgba(46,125,50,0.35)",
  amber:     "#8a6c1f",
  amberSoft: "rgba(196,154,42,0.15)",
  slate:     "#5a5e6a",
  slateLight:"#8e92a0",
  border:    "#e8e9ed",
  bg:        "#f8f9fc",
  white:     "#ffffff",
  text:      "#1a1d23",
  textMid:   "#5a5e6a",
  // Ledger-paper accents — give the fee surfaces a "challan" character that
  // matches the printed challans without changing any other modal.
  parchment:  "#fbf8f2",
  ledgerLine: "#ddd3bd",
};

export const ledgerMono = { fontFamily: "'IBM Plex Mono', monospace", fontVariantNumeric: "tabular-nums" };

// ── 2. Formatters ─────────────────────────────────────────────────────────────
export const fmt = (n) =>
  new Intl.NumberFormat("en-PK", { style: "currency", currency: "PKR", minimumFractionDigits: 0 }).format(n || 0);

export const fmtClass   = (c) => c ? c.replace(/Grade/i, "Class").trim() : "—";
export const fmtSection = (s) => s ? s.replace(/Section\s*/i, "").trim() : "—";

// ── 3. Shared UI primitives ───────────────────────────────────────────────────

// Icons
export const Icon = {
  Plus:  () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg>,
  Edit:  () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 3l4 4-7 7H10v-4l7-7z"/><path d="M4 20h16"/></svg>,
  Trash: () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 002 2h8a2 2 0 002-2l1-13M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>,
  Eye:   () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>,
  Print: () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><path d="M6 9V3h12v6"/><rect x="6" y="15" width="12" height="6" rx="2"/></svg>,
  Close: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Cash:  ({ size = 18 }) => <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="3"/><path d="M6 6v12M18 6v12"/></svg>,
  Layers: ({ size = 18 }) => <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 12l10 5 10-5"/><path d="M2 17l10 5 10-5"/></svg>,
  FileList: ({ size = 18 }) => <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6M9 17h6"/></svg>,
  BarChart: ({ size = 18 }) => <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M3 3v18h18"/><rect x="7" y="13" width="3" height="5"/><rect x="12" y="9" width="3" height="9"/><rect x="17" y="5" width="3" height="13"/></svg>,
  ArrowLeft: ({ size = 16 }) => <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  ArrowRight: ({ size = 14 }) => <svg width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg>,
};

// Button
export const Btn = ({ children, onClick, variant = "primary", size = "md", disabled = false, style: extra = {} }) => {
  const base = {
    display: "inline-flex", alignItems: "center", gap: "6px",
    fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    border: "none", borderRadius: "8px", transition: "opacity .15s",
    opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
    fontSize: size === "sm" ? "12px" : "13px",
    padding: size === "sm" ? "5px 12px" : "9px 18px",
  };
  const variants = {
    primary:  { background: C.navy,     color: "#fff" },
    blue:     { background: C.blue,     color: "#fff" },
    danger:   { background: C.red,      color: "#fff" },
    ghost:    { background: C.bg,       color: C.textMid, border: `1px solid ${C.border}` },
    success:  { background: C.green,    color: "#fff" },
    amber:    { background: C.amber,    color: "#fff" },
  };
  return (
    <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant], ...extra }}>
      {children}
    </button>
  );
};

// Badge
export const Badge = ({ children, color = "blue" }) => {
  const map = {
    blue:   [C.blueSoft,  C.blue],
    amber:  [C.amberSoft, C.amber],
    green:  [C.greenSoft, C.green],
    red:    [C.redSoft,   C.red],
    slate:  [C.bg,        C.slate],
  };
  const [bg, fg] = map[color] || map.slate;
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: bg, color: fg }}>
      {children}
    </span>
  );
};

// Avatar
export const Avatar = ({ name = "" }) => (
  <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.navy, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
    {name.charAt(0).toUpperCase()}
  </div>
);

// Card
export const Card = ({ children, style: extra = {} }) => (
  <div style={{ background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", ...extra }}>
    {children}
  </div>
);

export const CardHeader = ({ title, right }) => (
  <div style={{ padding: "16px 20px", borderRadius: "12px 12px 0 0", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: C.navy }}>
    <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "15px", fontWeight: 700, color: "#fff" }}>{title}</span>
    {right && <div>{right}</div>}
  </div>
);

// Table primitives
export const TH = ({ children, right }) => (
  <th style={{ textAlign: right ? "right" : "left", padding: "10px 16px", fontSize: "11px", fontWeight: 700, color: C.slate, background: C.bg, borderBottom: `2px solid ${C.border}`, textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
    {children}
  </th>
);
export const TD = ({ children, right, mono }) => (
  <td style={{ padding: "12px 16px", fontSize: "13px", color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: "middle", textAlign: right ? "right" : "left", fontFamily: mono ? "monospace" : undefined }}>
    {children}
  </td>
);

// Modal shell
export const Modal = ({ isOpen, onClose, title, subtitle, width = "680px", children, accent = false, bodyBg }) => {
  if (!isOpen) return null;
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(2px)" }}>
      <div style={{ background: bodyBg || C.white, borderRadius: "14px", width: `min(95vw, ${width})`, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ padding: "18px 24px", background: C.navy, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 10, borderRadius: "14px 14px 0 0", borderBottom: accent ? `3px solid ${C.amber}` : "none" }}>
          <div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: "16px", color: "#fff" }}>{title}</div>
            {subtitle && <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.65)", marginTop: "2px" }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", borderRadius: "6px", width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon.Close />
          </button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
};

// Label + field group
export const Field = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
    <label style={{ fontSize: "11px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>
    {children}
  </div>
);

export const inputStyle = { padding: "9px 12px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", color: C.text, background: C.white, outline: "none", width: "100%", boxSizing: "border-box" };
export const selectStyle = { ...inputStyle, cursor: "pointer" };
